import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Instagram } from "lucide-react";

// Moved out of Account page in iter 37 into "My Presence → Founder Story Wall".
// Same endpoints: POST /api/stories, GET /api/stories/mine, DELETE /api/stories/{id}
// plus /api/stories/{id}/instagram-card for the exported card image.
export default function StoryWall() {
  const [brand, setBrand] = useState("");
  const [story, setStory] = useState("");
  const [website, setWebsite] = useState("");
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/stories/mine").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!brand || !story) return;
    setBusy(true);
    try {
      await api.post("/stories", { brand, story, website: website || null });
      setBrand(""); setStory(""); setWebsite("");
      await load();
      toast.success("Story published");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this story?")) return;
    try { await api.delete(`/stories/${id}`); await load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <AppShell title="Founder Story Wall">
      <div className="w-full" data-testid="story-wall-page">
        <PageHero
          eyebrow="Founder Story Wall"
          icon={Megaphone}
          title={<>Share your brand on the <span className="text-grad">landing page</span></>}
          subtitle="Published stories appear publicly on cpginstitute.com and boost your brand's visibility to future founders. Approved stories also get a shareable Instagram card export."
        />

        <div className="surface p-6">
          <form onSubmit={submit} className="grid gap-3" data-testid="story-form">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Brand name</p>
              <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Warren's Salsa Co."
                data-testid="story-brand"
                className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Your story</p>
              <textarea value={story} onChange={(e) => setStory(e.target.value)} rows={4}
                placeholder="One or two sentences on what CPG Institute helped you do."
                data-testid="story-body"
                className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Website (optional)</p>
              <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourbrand.com"
                data-testid="story-website"
                className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <button type="submit" disabled={busy || !brand || !story}
              className="btn-amber self-start disabled:opacity-60"
              data-testid="story-submit">
              {busy ? "Publishing…" : "Publish to Story Wall"}
            </button>
          </form>

          {mine.length > 0 && (
            <div className="mt-8">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Your submissions</p>
              <div className="mt-3 grid gap-2">
                {mine.map((s) => (
                  <div key={s.id} data-testid={`my-story-${s.id}`}
                    className="border border-shell-border rounded-md p-3 text-sm flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{s.brand}</p>
                        {s.approved ? (
                          <span className="text-[9px] uppercase tracking-widest bg-green-100 text-green-700 rounded px-1.5 py-0.5 font-semibold">Live</span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-widest bg-pink-100 text-pink-700 rounded px-1.5 py-0.5 font-semibold">Pending review</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">"{s.story}"</p>
                      {s.approved && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/stories/${s.id}/instagram-card`}
                           target="_blank" rel="noreferrer"
                           className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                           data-testid={`my-story-ig-${s.id}`}>
                          <Instagram className="w-3 h-3" /> Download Instagram card ↗
                        </a>
                      )}
                    </div>
                    <button onClick={() => remove(s.id)}
                      className="text-xs text-muted-foreground hover:text-destructive"
                      data-testid={`my-story-del-${s.id}`}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
